import React, { useState } from "react";
import { Badge, Button, List, Modal, Space, Typography } from "antd";
import {
  BellOutlined,
  CloseOutlined,
  NotificationOutlined,
} from "@ant-design/icons";
import useCurrentUserData from "../hooks/useCurrentUserData";
import { renderLooseNode } from "../utils/rewards";
import { dashboardPalette } from "../theme";
import { UpdateEntryNotifications } from "../firebase-config";

interface NavbarProps {
  compact?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ compact = false }) => {
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const { userData } = useCurrentUserData();
  const notifications = userData?.Notifications ?? [];
  const unreadNotifications = notifications.filter(
    (notification) => !notification.Checked,
  );

  const closeNotifications = () => {
    setNotificationsOpen(false);
    const updatedNotifications = notifications.map((not: any) =>
      not.Checked === false ? { ...not, Checked: true } : not,
    );
    if (updatedNotifications.length > 0) {
      UpdateEntryNotifications(userData?.id!, updatedNotifications);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: compact ? "flex-end" : "flex-end",
        padding: compact ? "0" : "14px 20px 0",
      }}
    >
      <Button
        onClick={() => setNotificationsOpen(true)}
        shape="round"
        icon={
          unreadNotifications.length > 0 ? (
            <Badge count={unreadNotifications.length}>
              <BellOutlined />
            </Badge>
          ) : (
            <BellOutlined />
          )
        }
        style={{ borderRadius: 999 }}
      />

      <Modal
        open={notificationsOpen}
        onCancel={closeNotifications}
        centered
        footer={null}
        closable={false}
        styles={{
          body: {
            padding: 0,
            borderRadius: 24,
            overflow: "hidden",
            border: `1.5px solid ${dashboardPalette.panelBorder}`,
            boxShadow: "0 20px 60px rgba(30, 27, 58, 0.18)",
          },
        }}
      >
        <div
          style={{
            background: "linear-gradient(135deg, #7c5cfc 0%, #9b7dfc 100%)",
            color: "#fff",
            padding: "16px 18px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
          }}
        >
          <Space align="center" size={12}>
            <Typography.Title level={4} style={{ margin: 0, color: "#fff" }}>
              Notificaciones
            </Typography.Title>
            <Typography.Text style={{ color: "#fff", fontWeight: 800 }}>
              {unreadNotifications.length} nuevas
            </Typography.Text>
          </Space>
          <Button
            type="text"
            icon={<CloseOutlined style={{ color: "#fff" }} />}
            onClick={closeNotifications}
          />
        </div>

        <div
          style={{
            padding: 20,
            maxHeight: "calc(78vh - 136px)",
            overflowY: "auto",
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.65) 0%, rgba(236,240,248,0.82) 100%)",
          }}
        >
          {unreadNotifications.length === 0 ? (
            <div
              style={{
                minHeight: 220,
                borderRadius: 18,
                border: `2px dashed ${dashboardPalette.panelBorder}`,
                background: "rgba(255,255,255,0.72)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                padding: 24,
                gap: 12,
              }}
            >
              <div
                style={{
                  width: 58,
                  height: 58,
                  borderRadius: "50%",
                  background: "var(--dashboard-yellow)",
                  border: `3px solid ${dashboardPalette.accentYellowDark}`,
                  color: "var(--dashboard-header-blue)",
                  boxShadow: "0 4px 0 rgba(211, 158, 0, 0.85)",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <NotificationOutlined style={{ fontSize: 28 }} />
              </div>
              <Typography.Title
                level={5}
                style={{ margin: 0, color: dashboardPalette.headerBlue }}
              >
                No tienes notificaciones pendientes
              </Typography.Title>
              <Typography.Text
                style={{ color: dashboardPalette.textDark, maxWidth: 280 }}
              >
                Cuando haya novedades o actividades nuevas, aparecerán aquí.
              </Typography.Text>
            </div>
          ) : (
            <List
              dataSource={unreadNotifications}
              renderItem={(notif) => (
                <List.Item
                  style={{
                    marginBottom: 12,
                    padding: 14,
                    alignItems: "flex-start",
                    borderRadius: 18,
                    background: "rgba(255,255,255,0.88)",
                    border: `2px solid ${dashboardPalette.panelBorder}`,
                    boxShadow: "0 4px 0 rgba(62, 80, 147, 0.18)",
                  }}
                >
                  <List.Item.Meta
                    avatar={
                      <div
                        style={{
                          width: 42,
                          height: 42,
                          borderRadius: "50%",
                          background: "var(--dashboard-yellow)",
                          border: `2px solid ${dashboardPalette.accentYellowDark}`,
                          color: "var(--dashboard-header-blue)",
                          boxShadow: "0 3px 0 rgba(211, 158, 0, 0.72)",
                          display: "grid",
                          placeItems: "center",
                        }}
                      >
                        {renderLooseNode(notif.Icon, <NotificationOutlined />)}
                      </div>
                    }
                    title={
                      <Typography.Text
                        style={{
                          color: dashboardPalette.headerBlue,
                          fontWeight: 800,
                        }}
                      >
                        {notif.Title}
                      </Typography.Text>
                    }
                    description={
                      <Typography.Text
                        style={{ color: dashboardPalette.textDark }}
                      >
                        {notif.Description}
                      </Typography.Text>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </div>

        <div
          style={{
            padding: "0 20px 20px",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button
            type="primary"
            onClick={closeNotifications}
            style={{
              borderRadius: 14,
              background: "#7c5cfc",
              boxShadow: "0 3px 12px rgba(124, 92, 252, 0.35)",
            }}
          >
            Cerrar
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default Navbar;
